import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Loader2, Search } from "lucide-react"
import { api, type ExamPaper } from "@/lib/api"
import { toast } from "sonner"
import { ExamPaperPreviewDialog } from "@/components/ExamPaperPreviewDialog"
import { ExamPapersTable } from "@/components/ExamPapersTable"
import { PaperFilters } from "@/components/PaperFilters"
import { PaperPreviewPanel } from "@/components/PaperPreviewPanel"
import { PaginationControls } from "@/components/PaginationControls"
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels"

export default function BrowsePage() {
  const [papers, setPapers] = useState<ExamPaper[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedYear, setSelectedYear] = useState<string>("")
  const [selectedSubject, setSelectedSubject] = useState<string>("")
  const [previewOpen, setPreviewOpen] = useState(false)
  const [selectedPaper, setSelectedPaper] = useState<ExamPaper | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isMobile, setIsMobile] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  // Detect viewport size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    fetchPapers()
  }, [selectedYear, selectedSubject, searchQuery, currentPage])

  const fetchPapers = async () => {
    setLoading(true)
    try {
      const response = await api.getPapers({
        page: currentPage,
        per_page: 20,
        year: selectedYear || undefined,
        subject: selectedSubject || undefined,
        search: searchQuery || undefined
      })
      setPapers(response.data)
      setTotalPages(response.total_pages)
    } catch (error) {
      console.error("Failed to fetch papers:", error)
      toast.error("加载试卷失败")
    } finally {
      setLoading(false)
    }
  }

  const clearFilters = () => {
    setSelectedYear("")
    setSelectedSubject("")
    setSearchQuery("")
    setCurrentPage(1)
  }

  const handlePaperClick = (paper: ExamPaper) => {
    setSelectedPaper(paper)
    if (isMobile) {
      setShowPreview(false)
      setPreviewOpen(true)
    } else {
      setShowPreview(true)
      setPreviewOpen(false)
    }
  }

  const handleClosePreview = () => {
    setShowPreview(false)
    setSelectedPaper(null)
  }


  return (
    <div className="h-[100dvh] overflow-hidden bg-background flex flex-col">
      <header className="border-b border-border bg-card flex-shrink-0">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:h-16 sm:px-6">
          <h1 className="text-lg font-semibold text-foreground sm:text-xl">试卷库</h1>
        </div>
      </header>

      <main className="flex flex-1 flex-col overflow-hidden max-w-7xl mx-auto px-4 py-6 sm:px-6 sm:py-8 w-full min-h-0">
        {/* Filters */}
        <div className="mb-4 sm:mb-6 flex-shrink-0">
          <PaperFilters
            searchQuery={searchQuery}
            selectedYear={selectedYear}
            selectedSubject={selectedSubject}
            onSearchChange={(value) => {
              setSearchQuery(value)
              setCurrentPage(1)
            }}
            onYearChange={(value) => {
              setSelectedYear(value)
              setCurrentPage(1)
            }}
            onSubjectChange={(value) => {
              setSelectedSubject(value)
              setCurrentPage(1)
            }}
            onClearFilters={clearFilters}
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Papers Table with Split Panel */}
        {!loading && papers.length > 0 ? (
          showPreview && !isMobile ? (
            <PanelGroup direction="horizontal" className="grow rounded-lg border border-border overflow-hidden min-h-0">
              {/* Left Panel - Table */}
              <Panel defaultSize={50} minSize={30} maxSize={70}>
                <div className="h-full overflow-auto bg-card">
                  <ExamPapersTable
                    papers={papers}
                    selectedPaperId={selectedPaper?.id}
                    onView={handlePaperClick}
                  />
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </Panel>

              {/* Resize Handle */}
              <PanelResizeHandle className="w-2 bg-border hover:bg-primary/20 transition-colors" />

              {/* Right Panel - PDF Preview */}
              <Panel defaultSize={50} minSize={30}>
                <PaperPreviewPanel
                  paper={selectedPaper}
                  onClose={handleClosePreview}
                />
              </Panel>
            </PanelGroup>
          ) : (
            <div className="flex-1 overflow-auto">
              <div className="rounded-lg border border-border bg-card max-w-full">
                <ExamPapersTable
                  papers={papers}
                  selectedPaperId={selectedPaper?.id}
                  onView={handlePaperClick}
                />
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          )
        ) : !loading && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card py-16">
            <Search className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="mb-4 text-muted-foreground">未找到匹配的试卷</p>
            <Button variant="outline" onClick={clearFilters}>
              清除筛选条件
            </Button>
          </div>
        )}
      </main>

      {/* Preview Dialog for Mobile */}
      <ExamPaperPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} paper={selectedPaper} />
    </div>
  )
}
